import React from "react";
import { Helmet } from "react-helmet";
import YahooContactsApi, { YAHOO_MESSAGE, YAHOO_RESULT_KEY, YAHOO_RETURN_KEY, YAHOO_CALLBACK_PATH } from "../../assets/js/utils/contacts/yahooContactsApi";

/**
 * Yahoo Contacts sign-in return page: /integrations/yahoo/callback?code&state (or ?error).
 * Registered in the Yahoo Developer app as the OAuth redirect URI (dev and portal). Rendered without the
 * app chrome (it normally runs in the sign-in popup). Same flow as YahooContactsCallback.
 *
 * 1. Removes code/state from the address bar and history straight away.
 * 2. Posts them to the API with the user's normal Sendlinx auth headers. The server checks
 *    the state belongs to this user, redeems the code with the client secret
 *    and keeps the tokens, then runs the first contacts import. Nothing secret is stored in the browser.
 * 3. Tells the Send page (opener) the result and closes, on success and on errors alike. Without an opener (same-tab
 *    fallback) it goes back to the page that started the sign-in and reopens the picker.
 */
class YahooContactsCallback extends React.Component {
    constructor(props){
        super(props);
        this.history = props.history;
        this.started = false;
        let query = null;
        try{ query = new URLSearchParams(window.location.search); }catch(e){ query = null; }
        const param = (name) => {
            const value = query ? query.get(name) : null;
            return value && value.length <= 4096 ? value : null;
        };
        this.params = { code: param("code"), state: param("state"), error: param("error") };
        this.state = { phase: "working", message: "" };
    }
    componentDidMount(){
        if(this.started){ return; }
        this.started = true;
        try{ window.history.replaceState(null, "", YAHOO_CALLBACK_PATH); }catch(e){ }
        const { code, state, error } = this.params;
        if(!state){
            this.report({ ok: false, message: "This Yahoo sign-in link is incomplete. Please connect again from the Address Book." });
            return;
        }
        const redirectUri = window.location.origin + YAHOO_CALLBACK_PATH;   // must equal this environment's registered callback
        YahooContactsApi.completeConnect({ code, state, error, redirectUri }).then(status => {
            this.report({ ok: true, status });
        }).catch(err => {
            this.report({ ok: false, reason: err && err.reason, message: (err && err.message) || "Your Yahoo account could not be connected. Please try again." });
        });
    }
    hasOpener(){
        try{ return !!(window.opener && !window.opener.closed); }catch(e){ return false; }
    }
    report(result){
        const payload = { type: YAHOO_MESSAGE, ok: !!result.ok, message: result.message || "", reason: result.reason || null,
            status: result.status || null, at: Date.now() };
        // Same-origin only: the opener checks event.origin too.
        try{ if(this.hasOpener()){ window.opener.postMessage(payload, window.location.origin); } }catch(e){ }
        try{
            localStorage.setItem(YAHOO_RESULT_KEY, JSON.stringify(payload));
            setTimeout(() => { try{ localStorage.removeItem(YAHOO_RESULT_KEY); }catch(e){ } }, 2000);
        }catch(e){ }

        let returnTo = null;
        try{ returnTo = sessionStorage.getItem(YAHOO_RETURN_KEY); sessionStorage.removeItem(YAHOO_RETURN_KEY); }catch(e){ }
        if(returnTo !== null && !this.hasOpener()){
            // Same-tab fallback: only ever return to a path on this site.
            const path = /^\/[A-Za-z0-9/_-]*$/.test(returnTo) && returnTo.indexOf("//") !== 0 ? returnTo : "/";
            try{ sessionStorage.setItem(YAHOO_RESULT_KEY, JSON.stringify(payload)); }catch(e){ }
            this.history.replace(path+"?contacts=yahoo");
            return;
        }
        this.setState({ phase: result.ok ? "done" : "error", message: payload.message });
        // The sign-in popup closes itself whatever the result (consent denied, missing
        // permission, expired sign-in...); the Send page shows the outcome. The error stays visible
        // briefly first. Browsers ignore window.close() for a tab the user opened themselves,
        // so on a direct visit the message simply stays on screen.
        setTimeout(() => { try{ window.close(); }catch(e){ } }, result.ok ? 600 : 2500);
    }
    render(){
        const { phase, message } = this.state;
        const box = { maxWidth: "420px", margin: "96px auto", padding: "0 16px", textAlign: "center", fontFamily: "inherit" };
        return (
            <div style={box} role="status" aria-live="polite">
                <Helmet><title>Yahoo Contacts | Sendlinx</title></Helmet>
                {phase === "working" ? <p>Connecting your Yahoo contacts and importing them…</p> : null}
                {phase === "done" ? <>
                    <h1 style={{ fontSize: "20px" }}>Yahoo account connected</h1>
                    <p>You can close this window and pick recipients on the Send page.</p>
                    <button type="button" onClick={() => window.close()}>Close window</button>
                </> : null}
                {phase === "error" ? <>
                    <h1 style={{ fontSize: "20px" }}>Yahoo account not connected</h1>
                    <p>{message}</p>
                    <button type="button" onClick={() => window.close()}>Close window</button>
                </> : null}
            </div>
        );
    }
}

export default YahooContactsCallback;
